<template>
  <div>
    <CurrentLocationMapDialog
      :value="mapDialog"
      @close="mapDialog = false"
    />

    <AboutDialog
      :value="aboutDialog"
      @close="aboutDialog = false"
    />

    <v-app-bar
      app
      clipped-left
      dark
      color="primary"
      elevation="1"
      :class="{ 'app-bar': true, 'logo-panel': ! isMenuDrawerClosable() }"
    >
      <v-row>
        <v-col v-if="!isMenuDrawerClosable()">
          <v-row align="center">
            <v-card flat tile width="268px" height="64px" color="primary">
              <v-container class="pa-0" fill-height>
                <v-img src="../assets/img/geoclock_logo.png" height="30px" contain/>
              </v-container>
            </v-card>
            <v-toolbar-title class="pl-4">{{ pageTitle }}</v-toolbar-title>
          </v-row>
        </v-col>
        <v-col v-else :cols="$vuetify.breakpoint.name === 'xs' ? '8' : '10'">
          <v-row align="center">
            <v-col cols="6">
              <v-app-bar-nav-icon @click="drawer = !drawer"/>
            </v-col>
            <v-col cols="6">
              <v-img
                src="../assets/img/geoclock_logo.png"
                height="50px"
                width="150px"
                contain
              />
            </v-col>
          </v-row>
        </v-col>
        <v-col :cols="$vuetify.breakpoint.name === 'xs' ? '4' : '2'">
          <v-row class="flex-nowrap fill-height" justify="end" align="center">
            <v-btn v-if="isLocationFeatureActive" icon @click="mapDialog = true">
              <v-icon>my_location</v-icon>
            </v-btn>
            <v-btn icon @click="notificationsMenu = !notificationsMenu">
              <v-badge :value="notificationCount > 0" color="error" overlap left>
                <template v-slot:badge>
                  {{ notificationCount }}
                </template>
                <v-icon>notifications</v-icon>
              </v-badge>
            </v-btn>
          </v-row>
        </v-col>
      </v-row>
    </v-app-bar>

    <v-navigation-drawer
      v-model="drawer"
      app
      dark
      :temporary="isMenuDrawerClosable()"
      :permanent="!isMenuDrawerClosable()"
      height="100%"
      color="secondary"
      :style="isMobile() ? 'overflow-y:scroll;' : ''"
    >
      <template v-slot:prepend>
        <v-list nav>
          <v-list-item>
            <v-list-item-icon>
              <v-icon>account_circle</v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title>{{ `${employee.firstName} ${employee.lastName}` }}</v-list-item-title>
            </v-list-item-content>
          </v-list-item>
          <v-divider/>
          <v-list-item
            v-for="item in variableNavItems.filter(it => it.visible)"
            :key="item.title"
            :to="item.to"
            link
          >
            <v-list-item-icon>
              <v-icon>{{ item.icon }}</v-icon>
            </v-list-item-icon>
            <v-list-item-title>{{$t(item.title)}}</v-list-item-title>
          </v-list-item>
        </v-list>
      </template>

      <template v-slot:append>
        <v-list
          nav
          :style="isMobile() ? 'margin-bottom:30px' : ''"
        >
          <v-list-item
            v-for="item in fixedNavItems.filter(it => it.visible)"
            :key="item.title"
            @click="item.to.name === 'About' ? aboutDialog = !aboutDialog : $router.push(item.to)"
          >
            <v-list-item-icon>
              <v-icon>{{ item.icon }}</v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title>{{ $t(item.title) }}</v-list-item-title>
            </v-list-item-content>
          </v-list-item>
        </v-list>
      </template>
    </v-navigation-drawer>

    <v-navigation-drawer
      v-model="notificationsMenu"
      app
      right
      temporary
      :width="notificationDrawerWidth"
      height="100%"
    >
      <NotificationList :notifications="notifications" @close="notificationsMenu = false"/>
    </v-navigation-drawer>
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex';

import find from 'lodash/find';

import displayAdaptable from '@/mixins/displayAdaptable';
import hasLocationFeature from '@/mixins/location/hasLocationFeature';
import hasWorkScheduleFeatures from '@/mixins/hasWorkScheduleFeatures';

import AboutDialog from '@/components/dialogs/AboutDialog.vue';
import CurrentLocationMapDialog from '@/components/dialogs/CurrentLocationMapDialog.vue';
import NotificationList from '@/components/NotificationList.vue';
import AuthorisationUtil from '@/util/authorisation-util';

export default {
  name: 'NavigationFrame',
  mixins: [displayAdaptable, hasLocationFeature, hasWorkScheduleFeatures],
  components: { CurrentLocationMapDialog, AboutDialog, NotificationList },
  props: {
    manager: Boolean,
    clock: Boolean,
  },

  data() {
    return {
      updated: false,
      drawer: false,
      notificationsMenu: false,
      mapDialog: false,
      aboutDialog: false,
      variableNavItems: [
        {
          title: 'Navigation.EMPLOYEE_CLOCKING_TAB_TITLE',
          icon: 'alarm',
          visible: false,
          to: null,
        },
        {
          title: 'Navigation.EMPLOYEE_TIME_TAB_TITLE',
          icon: 'history',
          visible: false,
          to: null,
        },
        {
          title: 'Navigation.EMPLOYEE_WORK_SCHEDULES_TAB_TITLE',
          icon: 'calendar_today',
          visible: false,
          to: null,
        },
        {
          title: 'Navigation.MANAGER_APPROVAL_TABLE_TITLE',
          icon: 'list_alt',
          visible: false,
          to: { name: 'Approvals' },
        },
        {
          title: 'Navigation.MANAGER_CLOCKING_TAB_TITLE',
          icon: 'alarm',
          visible: false,
          to: { name: 'ApproverClocking' },
        },
        {
          title: 'Navigation.MANAGER_WORK_SCHEDULES_TAB_TITLE',
          icon: 'calendar_today',
          visible: false,
          to: { name: 'ApproverWorkSchedules' },
        },
      ],
      fixedNavItems: [
        {
          title: 'dialogs.about.about',
          icon: 'info',
          visible: true,
          to: { name: 'About' },
        },
        {
          title: 'Navigation.settings',
          icon: 'settings',
          visible: true,
          to: { name: 'Settings' },
        },
        {
          title: 'Navigation.logOut',
          icon: 'power_settings_new',
          visible: true,
          to: { name: 'Login', params: { logout: true } },
        },
      ],
    };
  },

  computed: {
    ...mapState({
      pageTitle: (state) => state.menuData.pageTitle,
      employee: (state) => state.employee,
      profileLoading: (state) => state.profileLoading,
      notificationCount: (state) => state.notificationCount,
      notifications: (state) => state.notifications,
    }),

    notificationDrawerWidth() {
      switch (this.$vuetify.breakpoint.name) {
        case 'xl': {
          return '38%';
        }
        case 'lg': {
          return '53%';
        }
        default: {
          return '100%';
        }
      }
    },
  },

  watch: {
    profileLoading(value) {
      if (!value) {
        this.fetchNotifications();
        this.calculateVisibleItems();
      }
    },
    isWorkScheduleEmployeeViewFeatureEnabled(value) {
      if (value) {
        this.calculateVisibleItems();
      }
    },
    isWorkScheduleManagerViewFeatureEnabled(value) {
      if (value) {
        this.managerTabVisible();
      }
    },

  },

  methods: {
    ...mapActions(['fetchNotifications', 'fetchLicense', 'fetchVersion', 'fetchFeatureSwitches']),

    calculateVisibleItems() {
      this.clockTabVisible();
      this.managerTabVisible();
    },

    clockTabVisible() {
      if (AuthorisationUtil.employeeHasCaptureTimeAuthority()
        || AuthorisationUtil.employeeHasViewTimeAuthority()) {
        let timeItem = find(this.variableNavItems,
          ['title', 'Navigation.EMPLOYEE_CLOCKING_TAB_TITLE']);
        timeItem.visible = true;
        timeItem.to = { name: 'Clock' };

        timeItem = find(this.variableNavItems,
          ['title', 'Navigation.EMPLOYEE_TIME_TAB_TITLE']);
        timeItem.visible = true;
        timeItem.to = { name: 'EmployeeTimePairs' };

        timeItem = find(this.variableNavItems,
          ['title', 'Navigation.EMPLOYEE_WORK_SCHEDULES_TAB_TITLE']);
        timeItem.to = { name: 'EmployeeWorkSchedules' };
        timeItem.visible = this.isWorkScheduleEmployeeViewFeatureEnabled;
      }

      const workSchedulesItem = find(this.variableNavItems,
        ['title', 'Navigation.EMPLOYEE_WORK_SCHEDULES_TAB_TITLE']);
      workSchedulesItem.to = { name: 'EmployeeWorkSchedules' };
      workSchedulesItem.visible = this.isWorkScheduleEmployeeViewFeatureEnabled;
    },

    managerTabVisible() {
      const approvalsItem = find(this.variableNavItems,
        ['title', 'Navigation.MANAGER_APPROVAL_TABLE_TITLE']);
      approvalsItem.visible = AuthorisationUtil.isManager();

      const clockingItem = find(this.variableNavItems,
        ['title', 'Navigation.MANAGER_CLOCKING_TAB_TITLE']);
      clockingItem.visible = AuthorisationUtil.managerCanClockEmployees();

      const workSchedulesItem = find(this.variableNavItems,
        ['title', 'Navigation.MANAGER_WORK_SCHEDULES_TAB_TITLE']);
      workSchedulesItem.visible = AuthorisationUtil.isManager()
        && this.isWorkScheduleManagerViewFeatureEnabled;
    },
  },

  mounted() {
    if (!this.profileLoading) {
      this.fetchNotifications();
      this.calculateVisibleItems();
    }
    this.fetchLicense();
    this.fetchVersion();
    this.fetchFeatureSwitches();
  },
};

</script>

<style scoped lang="scss">
.app-bar {
  z-index: 10;
}

.v-navigation-drawer {
  padding-top: 65px;
}

.v-navigation-drawer--temporary {
  z-index: 5;
}
</style>
